// The CUSTOM_API provider's own form: what to call, with what, and how to read the answer.
//
// The shared integration fields (one secret box, one "settings" textarea) cannot
// describe this — a Custom API row is a whole request: URL, method, headers, a
// templated body in one of three formats, a response mapping, and one of six auth
// schemes whose secrets live in different encrypted columns. So it gets a form of
// its own, and the configJson it produces is exactly what CustomApiConfig.parse
// reads (core-service …/crm/customapi/CustomApiConfig.java).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, AlertTriangle, ChevronRight } from 'lucide-react';
import type { CrmProvider, CrmAuthType } from 'api/services/crmProviders.service';
import {
  CustomApiForm as Form, KV, formFromConfig, buildConfig, CUSTOM_API_AUTH, AUTH_FIELDS,
  PLACEHOLDER_HELP, PLACEHOLDER_SYNTAX, SECRET_HEADER,
} from './customApiShapes';

const input = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm';
const lbl = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';
const hint = 'text-[11px] mt-1';
const muted = { color: 'var(--text-muted)' } as const;

const BODY_EXAMPLE: Record<string, string> = {
  JSON: '{\n  "user_id": "{{call.caller|digits}}",\n  "call_id": "{{call.id}}",\n  "name": "{{lead.name|trim|max:200}}",\n  "issue": "{{agent.issueType}}"\n}',
  FORM_URLENCODED: '{\n  "name": "{{lead.name}}",\n  "phone": "{{call.caller|digits}}",\n  "email": "{{lead.email?}}"\n}',
  XML: '<Request>\n  <Name>{{lead.name}}</Name>\n  <Phone>{{call.caller|digits}}</Phone>\n</Request>',
};

/** Name/value rows — headers, response fields. */
const Rows: React.FC<{
  rows: KV[]; onChange: (rows: KV[]) => void; keyPh: string; valuePh: string;
  warn?: (key: string) => string | null; addLabel: string;
}> = ({ rows, onChange, keyPh, valuePh, warn, addLabel }) => (
  <div className="space-y-1.5">
    {rows.map((r, i) => {
      const w = warn ? warn(r.key) : null;
      return (
        <div key={i}>
          <div className="flex gap-1.5">
            <input className={input + ' font-mono text-xs'} placeholder={keyPh} value={r.key}
                   onChange={e => onChange(rows.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} />
            <input className={input + ' font-mono text-xs'} placeholder={valuePh} value={r.value}
                   onChange={e => onChange(rows.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
            <button type="button" className="tl-icon-btn" title="Remove"
                    onClick={() => onChange(rows.filter((_, j) => j !== i))}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          {w && <p className="text-[11px] mt-0.5 text-red-600 dark:text-red-400">{w}</p>}
        </div>
      );
    })}
    <button type="button" className="tl-btn tl-btn-secondary tl-btn-sm"
            onClick={() => onChange([...rows, { key: '', value: '' }])}>
      <Plus className="w-3.5 h-3.5" /> {addLabel}
    </button>
  </div>
);

const Head: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-[13px] font-semibold text-gray-900 dark:text-white pt-2 border-t border-gray-100 dark:border-gray-700">
    {children}
  </h3>
);

export interface CustomApiFormProps {
  provider: CrmProvider;
  /** Patches the row being edited: configJson, authType and the write-only secrets. */
  onChange: (patch: Partial<CrmProvider>) => void;
  /** What the server would refuse, so the parent can hold the Save. */
  onProblems: (problems: string[]) => void;
}

const CustomApiForm: React.FC<CustomApiFormProps> = ({ provider, onChange, onProblems }) => {
  // Read once from the stored row. From then on the form is the source and
  // configJson is derived from it — never the other way round, or a half-typed
  // body template (not yet valid JSON) would be thrown away on every keystroke.
  const [f, setF] = useState<Form>(() => formFromConfig(provider.configJson));
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const authType = (provider.authType || 'NONE') as CrmAuthType;
  const auth = AUTH_FIELDS[authType] || AUTH_FIELDS.NONE;
  const fetch = f.mode === 'FETCH';

  const built = useMemo(() => buildConfig(f, authType), [f, authType]);
  const configJson = useMemo(() => JSON.stringify(built.config, null, 2), [built]);

  // Lifted on every change, so Save always sends what is on screen.
  useEffect(() => {
    if (configJson !== provider.configJson) onChange({ configJson });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configJson]);
  // Keyed on the text of the list: a new array every render must not re-lift it.
  const problemsKey = built.problems.join('\n');
  useEffect(() => {
    onProblems(built.problems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemsKey]);

  const set = (patch: Partial<Form>) => setF(prev => ({ ...prev, ...patch }));

  const setMode = (mode: Form['mode']) => {
    // PUSH only takes POST/PUT; GET is FETCH's default. Keep the method if it is
    // still legal, otherwise move to the mode's default rather than leave an
    // invalid one selected.
    const ok = mode === 'FETCH' ? ['GET', 'POST', 'PUT'] : ['POST', 'PUT'];
    set({ mode, method: ok.includes(f.method) ? f.method : (mode === 'FETCH' ? 'GET' : 'POST') });
  };

  /** Inserts a placeholder where the cursor is in the body template. */
  const insert = (token: string) => {
    const el = bodyRef.current;
    const at = el ? el.selectionStart : f.bodyText.length;
    const end = el ? el.selectionEnd : at;
    set({ bodyText: f.bodyText.slice(0, at) + token + f.bodyText.slice(end) });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.selectionStart = el.selectionEnd = at + token.length;
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className={lbl}>What it does</label>
        <div className="grid grid-cols-2 gap-1.5">
          {([
            ['PUSH', 'Push a lead / form', 'Once per call, after the agent captures it. Retried if there is no answer.'],
            ['FETCH', 'In-call lookup tool', 'The agent calls it mid-call and hears only the fields you map. Read-only, no retries.'],
          ] as const).map(([m, title, desc]) => (
            <button key={m} type="button" onClick={() => setMode(m)}
              className={`text-left rounded-lg border p-2.5 ${f.mode === m
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}>
              <span className="block text-sm font-medium">{title}</span>
              <span className="block text-[11px]" style={muted}>{desc}</span>
            </button>
          ))}
        </div>
      </div>

      {fetch && (
        <div>
          <label className={lbl}>Tool name</label>
          <input className={input + ' font-mono text-xs'} value={f.toolName} placeholder="balance_lookup"
                 onChange={e => set({ toolName: e.target.value })} />
          <p className={hint} style={muted}>
            Lowercase, unique in this organisation. The agent’s tool posts its arguments to{' '}
            <code>/api/v1/agent-tools/&#123;agent_id&#125;/api/{f.toolName || '<tool name>'}</code> with the org API key.
          </p>
        </div>
      )}

      <Head>Request</Head>
      <div className="grid grid-cols-[110px_1fr] gap-2">
        <div>
          <label className={lbl}>Method</label>
          <select className={input} value={f.method} onChange={e => set({ method: e.target.value })}>
            {(fetch ? ['GET', 'POST', 'PUT'] : ['POST', 'PUT']).map(m => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>URL</label>
          <input className={input + ' font-mono text-xs'} value={f.url}
                 placeholder={fetch ? 'https://api.example.com/accounts/{{args.msisdn|digits}}/balance' : 'https://api.example.com/v1/leads'}
                 onChange={e => set({ url: e.target.value })} />
        </div>
      </div>
      <p className={hint + ' -mt-1'} style={muted}>
        Placeholders are allowed in the path and query (URL-encoded) — never in the host.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={lbl}>Timeout (ms) <span style={muted}>— optional</span></label>
          <input className={input} inputMode="numeric" value={f.timeoutMs}
                 placeholder={fetch ? '4000 (max 8000)' : '5000 (max 10000)'}
                 onChange={e => set({ timeoutMs: e.target.value.replace(/[^0-9]/g, '') })} />
        </div>
        <div>
          <label className={lbl}>Request-id query param <span style={muted}>— optional</span></label>
          <input className={input + ' font-mono text-xs'} value={f.requestIdParam} placeholder="request_id"
                 onChange={e => set({ requestIdParam: e.target.value })} />
        </div>
      </div>

      <div>
        <label className={lbl}>Headers <span style={muted}>— non-secret only</span></label>
        <Rows rows={f.headers} onChange={headers => set({ headers })} keyPh="X-Header-Name" valuePh="value"
              addLabel="Add header"
              warn={k => (k.trim() && SECRET_HEADER.test(k)
                ? 'Looks like a credential — the server refuses it. Put secrets in Authentication below; those are encrypted.'
                : null)} />
      </div>

      {f.method !== 'GET' && (
        <>
          <Head>Body</Head>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={lbl}>Format</label>
              <select className={input} value={f.bodyFormat}
                      onChange={e => set({ bodyFormat: e.target.value as Form['bodyFormat'] })}>
                <option value="JSON">JSON</option>
                <option value="FORM_URLENCODED">Form (x-www-form-urlencoded)</option>
                <option value="XML">XML</option>
              </select>
            </div>
          </div>
          <div>
            <label className={lbl}>
              Body template {f.bodyFormat === 'XML' ? '(the XML document)' : f.bodyFormat === 'FORM_URLENCODED' ? '(a flat JSON object — one key per form field)' : '(a JSON object)'}
            </label>
            <textarea ref={bodyRef} rows={9} className={input + ' font-mono text-xs'} value={f.bodyText}
                      placeholder={BODY_EXAMPLE[f.bodyFormat]} spellCheck={false}
                      onChange={e => set({ bodyText: e.target.value })} />
            <p className={hint} style={muted}>
              {f.bodyFormat === 'XML'
                ? 'Values are XML-escaped when substituted.'
                : 'Values are escaped by construction. A string that is exactly one placeholder becomes that value; an optional missing one becomes null' + (f.bodyFormat === 'FORM_URLENCODED' ? ' and is left out of the form.' : '.')}
            </p>
          </div>
        </>
      )}

      <details className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 group">
        <summary className="text-[12px] font-medium cursor-pointer flex items-center gap-1 list-none">
          <ChevronRight className="w-3.5 h-3.5 group-open:rotate-90 transition-transform" /> Placeholders
          {f.method !== 'GET' && <span className="font-normal" style={muted}>— click one to insert it into the body</span>}
        </summary>
        <ul className="mt-2 space-y-1">
          {PLACEHOLDER_HELP.filter(p => fetch || !p.fetchOnly).map(p => (
            <li key={p.token} className="text-[11.5px] flex gap-2 items-baseline">
              <button type="button" disabled={f.method === 'GET'} onClick={() => insert(p.token)}
                      className="font-mono text-blue-700 dark:text-blue-300 hover:underline disabled:no-underline disabled:text-gray-500 shrink-0">
                {p.token}
              </button>
              <span style={muted}>{p.means}</span>
            </li>
          ))}
        </ul>
        <p className={hint} style={muted}>{PLACEHOLDER_SYNTAX}</p>
      </details>

      <Head>Response</Head>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={lbl}>Format</label>
          <select className={input} value={f.responseFormat}
                  onChange={e => set({ responseFormat: e.target.value as Form['responseFormat'] })}>
            <option value="JSON">JSON — dotted paths</option>
            <option value="XML">XML — XPath</option>
          </select>
        </div>
      </div>
      <p className={hint + ' -mt-1'} style={muted}>
        {f.responseFormat === 'XML'
          ? 'Paths are XPath, e.g. //Result/Balance (namespaces ignored).'
          : 'Paths are dotted, e.g. dto.toolResult.success. "a|b" takes the first that exists.'}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={lbl}>Success path</label>
          <input className={input + ' font-mono text-xs'} value={f.successPath} placeholder="toolResult.success"
                 onChange={e => set({ successPath: e.target.value })} />
        </div>
        <div>
          <label className={lbl}>Success value</label>
          <input className={input + ' font-mono text-xs'} value={f.successValue} placeholder="true"
                 onChange={e => set({ successValue: e.target.value })} />
        </div>
        {!fetch && (
          <div>
            <label className={lbl}>Reference path</label>
            <input className={input + ' font-mono text-xs'} value={f.referencePath} placeholder="toolResult.referenceNumber"
                   onChange={e => set({ referencePath: e.target.value })} />
          </div>
        )}
        <div>
          <label className={lbl}>Error path</label>
          <input className={input + ' font-mono text-xs'} value={f.errorPath} placeholder="toolResult.errorMessages"
                 onChange={e => set({ errorPath: e.target.value })} />
        </div>
        {!fetch && (
          <div>
            <label className={lbl}>Message path</label>
            <input className={input + ' font-mono text-xs'} value={f.messagePath} placeholder="response"
                   onChange={e => set({ messagePath: e.target.value })} />
          </div>
        )}
      </div>
      {!fetch && (
        <p className={hint + ' -mt-1'} style={muted}>
          With no success path, any 2xx counts as sent. With one, a 2xx that lacks it is recorded
          Unconfirmed and never resent — a second record is worse than a missing reference.
        </p>
      )}
      {fetch && (
        <>
          <div>
            <label className={lbl}>Fields the agent sees</label>
            <Rows rows={f.responseFields} onChange={responseFields => set({ responseFields })}
                  keyPh="balance" valuePh={f.responseFormat === 'XML' ? '//Result/Balance' : 'data.balance'}
                  addLabel="Add field" />
            <p className={hint} style={muted}>
              The ONLY values returned to the agent, as <code>{'{"ok": true, "data": {…}}'}</code>. Nothing else
              from the reply reaches it.
            </p>
          </div>
          <div>
            <label className={lbl}>Say on error <span style={muted}>— optional</span></label>
            <input className={input} value={f.sayOnError} placeholder="Sorry, I can't check that right now."
                   onChange={e => set({ sayOnError: e.target.value })} />
          </div>
        </>
      )}

      <Head>Authentication</Head>
      <div>
        <select className={input} value={authType}
                onChange={e => onChange({ authType: e.target.value as CrmAuthType })}>
          {CUSTOM_API_AUTH.map(a => <option key={a} value={a}>{AUTH_FIELDS[a].label}</option>)}
        </select>
        <p className={hint} style={muted}>{auth.hint} Secrets are encrypted and never shown again.</p>
      </div>
      {authType === 'API_KEY' && (
        <div>
          <label className={lbl}>Header name</label>
          <input className={input + ' font-mono text-xs'} value={f.apiKeyHeader} placeholder="X-API-Key"
                 onChange={e => set({ apiKeyHeader: e.target.value })} />
        </div>
      )}
      {auth.clientId && (
        <div>
          <label className={lbl}>{auth.clientId}</label>
          <input className={input + ' font-mono text-xs'} value={provider.clientId || ''} autoComplete="off"
                 onChange={e => onChange({ clientId: e.target.value })} />
        </div>
      )}
      {auth.clientSecret && (
        <div>
          <label className={lbl}>{auth.clientSecret}</label>
          <input type="password" autoComplete="new-password" className={input + ' font-mono text-xs'}
                 value={provider.clientSecret || ''}
                 onChange={e => onChange({ clientSecret: e.target.value })}
                 placeholder={provider.hasClientSecret ? '•••••• already set — leave blank to keep' : ''} />
        </div>
      )}
      {auth.secret && (
        <div>
          <label className={lbl}>{auth.secret}</label>
          <input type="password" autoComplete="new-password" className={input + ' font-mono text-xs'}
                 value={provider.refreshToken || ''}
                 onChange={e => onChange({ refreshToken: e.target.value })}
                 placeholder={provider.hasRefreshToken ? '•••••• already set — leave blank to keep' : ''} />
          {!provider.hasRefreshToken && !provider.refreshToken && (
            <p className={hint + ' text-amber-600 dark:text-amber-400'}>Nothing stored yet — requests go out without it.</p>
          )}
        </div>
      )}
      {authType === 'HMAC_SHA256' && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={lbl}>Signature header</label>
            <input className={input + ' font-mono text-xs'} value={f.hmacHeader} placeholder="X-Signature"
                   onChange={e => set({ hmacHeader: e.target.value })} />
          </div>
          <div>
            <label className={lbl}>Signs</label>
            <select className={input} value={f.hmacSigns}
                    onChange={e => set({ hmacSigns: e.target.value as Form['hmacSigns'] })}>
              <option value="BODY">The raw body</option>
              <option value="METHOD_PATH_TIMESTAMP_BODY">METHOD \n path?query \n timestamp \n body</option>
            </select>
          </div>
          {f.hmacSigns === 'METHOD_PATH_TIMESTAMP_BODY' && (
            <div>
              <label className={lbl}>Timestamp header</label>
              <input className={input + ' font-mono text-xs'} value={f.hmacTimestampHeader} placeholder="X-Timestamp"
                     onChange={e => set({ hmacTimestampHeader: e.target.value })} />
            </div>
          )}
          <div>
            <label className={lbl}>Encoding</label>
            <select className={input} value={f.hmacEncoding}
                    onChange={e => set({ hmacEncoding: e.target.value as Form['hmacEncoding'] })}>
              <option value="hex">hex</option>
              <option value="base64">base64</option>
            </select>
          </div>
          <div>
            <label className={lbl}>Prefix <span style={muted}>— optional</span></label>
            <input className={input + ' font-mono text-xs'} value={f.hmacPrefix} placeholder="sha256="
                   onChange={e => set({ hmacPrefix: e.target.value })} />
          </div>
        </div>
      )}
      {authType === 'OAUTH2_CLIENT_CREDENTIALS' && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={lbl}>Token URL</label>
            <input className={input + ' font-mono text-xs'} value={f.oauthTokenUrl} placeholder="https://auth.example.com/oauth/token"
                   onChange={e => set({ oauthTokenUrl: e.target.value })} />
          </div>
          <div>
            <label className={lbl}>Scope <span style={muted}>— optional</span></label>
            <input className={input + ' font-mono text-xs'} value={f.oauthScope}
                   onChange={e => set({ oauthScope: e.target.value })} />
          </div>
        </div>
      )}

      {!fetch && (
        <details className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 group">
          <summary className="text-[12px] font-medium cursor-pointer flex items-center gap-1 list-none">
            <ChevronRight className="w-3.5 h-3.5 group-open:rotate-90 transition-transform" /> Input rules
            <span className="font-normal" style={muted}>— optional, advanced</span>
          </summary>
          <textarea rows={5} className={input + ' font-mono text-xs mt-2'} value={f.inputRulesText} spellCheck={false}
                    placeholder={'[\n  { "field": "affected_msisdn", "normalize": "digits",\n    "pattern": "^(263|0)?7[0-9]{8}$",\n    "message": "not a Zimbabwe mobile number — ask again" }\n]'}
                    onChange={e => set({ inputRulesText: e.target.value })} />
          <p className={hint} style={muted}>
            Checked on the call: a captured field that fails its pattern is refused with the message, so the
            agent can ask again. After the call, a failure is recorded Invalid and nothing is sent.
          </p>
        </details>
      )}

      {built.placeholders.length > 0 && (
        <p className="text-[11px]" style={muted}>
          Uses: {built.placeholders.map(p => <code key={p} className="mr-1.5">{p}</code>)}
          {!fetch && built.placeholders.includes('call.caller') && (
            <span className="block mt-0.5">
              {'{{call.caller}}'} is usually not known until hang-up, so the push is held as “Awaiting caller”
              and sent when the call record arrives.
            </span>
          )}
        </p>
      )}
      {built.problems.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-[12px] text-amber-900
                        dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <div className="flex gap-1.5 font-medium"><AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> Not saveable yet</div>
          <ul className="list-disc ml-5 mt-1 space-y-0.5">
            {built.problems.map(p => <li key={p}>{p}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomApiForm;
